import React, { useState } from 'react';
import { Box, Typography, IconButton, Button, Tooltip, Chip, useMediaQuery } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PaidIcon from '@mui/icons-material/PaidOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EditIcon from '@mui/icons-material/Edit';
import { StatusChip } from '../ui';
import InvoiceEmailStatusChip from './InvoiceEmailStatusChip';
import InvoiceActionMenu from './InvoiceActionMenu';
import { getInvoiceActionState } from './invoiceActionState';
import { formatCurrency } from '../../utils/currencyFormatter';

const statusLabel = (status, t, needsManualReview) => {
  if (status === 'processing_issue') return needsManualReview ? t('Needs manual review') : t('Issuing…');
  if (status === 'processing_payment') return t('Processing payment');
  return t(status.charAt(0).toUpperCase() + status.slice(1));
};

const rowBorderSx = { borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 'none' } };

// design_handoff_invoice_actions/README.md — Step 10: the eye/View icon is
// gone (clicking the row opens the detail dialog); the primary button and
// quick icon follow the invoice's next likely action instead of always
// being "Mark paid"; the ⋯ menu (InvoiceActionMenu, shared with the detail
// dialog per design_handoff_invoice_detail/README.md Step 11) is always
// present outside processing states.
// CHANGES.md §6.2 — processing_issue/processing_payment keep their own
// badge + Refresh icon, no primary/quick/menu, unchanged from before.
const InvoiceRow = ({
  t, i18n, invoice, needsManualReview, onView, onEdit, onIssue, onDelete, onMarkPaid, onVoid,
  onSendEmail, onRemind, onDownloadPdf, onPrintLabel, onDownloadLabel, onRefresh, tourAnchor,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  // Below the theme's `sm` breakpoint (900px) the one-line layout can't fit
  // number + chips + date + amount + four actions, so the row stacks instead:
  // identity and amount on top, actions on their own line. A literal query
  // (matching DataTableCard) keeps this working in specs without a ThemeProvider.
  const isMobile = useMediaQuery('(max-width:899.95px)');
  const {
    isDraft, isPaid, isVoid, isProcessing, isProcessingIssue,
    isIssuedNotSent, isIssuedSent, canDownloadPdf,
  } = getInvoiceActionState(invoice);

  const closeMenu = () => setMenuAnchor(null);

  const identity = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, rowGap: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" noWrap>{invoice.invoiceNumber || t('Draft')}</Typography>
        <StatusChip status={invoice.status} label={statusLabel(invoice.status, t, needsManualReview)} />
        {invoice.emailRecipients && invoice.emailRecipients.length > 0 ? (
          // One chip per recipient — a bounce on one address must stay
          // visible here even when another recipient on the same send
          // opened it later; collapsing to a single "most recent" status
          // is exactly the bug this replaced. Prefixed with Email:/CC: since
          // (unlike the detail dialog) the row shows no address to tell two
          // recipients' chips apart otherwise.
          invoice.emailRecipients.map((r) => (
            <InvoiceEmailStatusChip
              key={r.email} t={t} emailStatus={r.status} emailSentAt={invoice.emailSentAt}
              prefix={r.type === 'cc' ? `${t('CC', { defaultValue: 'CC' })}:` : `${t('Email')}:`}
            />
          ))
        ) : (
          <InvoiceEmailStatusChip t={t} emailStatus={invoice.emailStatus} emailSentAt={invoice.emailSentAt} />
        )}
        {isProcessingIssue && needsManualReview && (
          <Chip size="small" color="error" variant="outlined" label={t('Backend support required')} />
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" noWrap>
        {invoice.recipient?.name || ''} · {invoice.invoiceType === 'custom' ? t('Custom') : t('Order based')}
      </Typography>
    </>
  );

  const created = (
    <Typography variant="caption" color="text.secondary" sx={isMobile ? { display: 'block', mt: 0.25 } : { flex: 1 }}>
      {t('Created')}: {invoice.createdAt ? new Date(invoice.createdAt).toLocaleDateString() : t('N/A')}
    </Typography>
  );

  const amount = (
    <Typography variant="subtitle1" sx={isMobile ? { flexShrink: 0, textAlign: 'right' } : { width: 100, textAlign: 'right' }}>
      {formatCurrency(invoice.total, invoice.currency, i18n.language)}
    </Typography>
  );

  // -- Primary slot + quick icon: follows the invoice's most likely next
  // action instead of a fixed "Mark paid" (design_handoff_invoice_actions/README.md). --
  const emailInvoiceLabel = t('Email Invoice', { defaultValue: 'Email Invoice' });
  let primarySlot = null;
  let quickSlot = null;
  if (isDraft) {
    primarySlot = (
      <Button size="small" variant="contained" startIcon={<SendIcon fontSize="small" />} onClick={() => onIssue(invoice)}>
        {t('Issue')}
      </Button>
    );
    quickSlot = { title: t('Edit'), icon: <EditIcon fontSize="small" />, onClick: () => onEdit(invoice) };
  } else if (isIssuedNotSent) {
    primarySlot = (
      <Button size="small" variant="contained" startIcon={<MailOutlineIcon fontSize="small" />} onClick={() => onSendEmail(invoice)}>
        {emailInvoiceLabel}
      </Button>
    );
    quickSlot = { title: t('Mark paid'), icon: <PaidIcon fontSize="small" />, color: 'success.main', onClick: () => onMarkPaid(invoice) };
  } else if (isIssuedSent) {
    primarySlot = (
      <Button size="small" variant="contained" color="success" startIcon={<PaidIcon fontSize="small" />} onClick={() => onMarkPaid(invoice)}>
        {t('Mark paid')}
      </Button>
    );
    quickSlot = { title: t('Send reminder', { defaultValue: 'Send reminder' }), icon: <NotificationsNoneIcon fontSize="small" />, color: 'primary.main', onClick: () => onRemind(invoice) };
  } else if (isPaid) {
    primarySlot = (
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96, textAlign: 'center' }}>
        {t('Paid')} {invoice.paidAt ? new Date(invoice.paidAt).toLocaleDateString() : ''}
      </Typography>
    );
    quickSlot = { title: emailInvoiceLabel, icon: <MailOutlineIcon fontSize="small" />, onClick: () => onSendEmail(invoice) };
  } else if (isVoid) {
    primarySlot = (
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96, textAlign: 'center' }}>
        {t('Voided')} {invoice.updatedAt ? new Date(invoice.updatedAt).toLocaleDateString() : ''}
      </Typography>
    );
  }

  const actions = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        flexShrink: 0,
        ...(isMobile
          // Full-width action line with the 44px minimum tap target on the
          // icon buttons (same bump DataTableCardMobile applies).
          ? { justifyContent: 'flex-end', flexWrap: 'wrap', '& .MuiIconButton-root': { minWidth: 44, minHeight: 44 } }
          : {}),
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {primarySlot}

      {quickSlot && (
        <Tooltip title={quickSlot.title}>
          <IconButton
            size="small"
            onClick={quickSlot.onClick}
            aria-label={quickSlot.title}
            sx={quickSlot.color ? { color: quickSlot.color } : undefined}
          >
            {quickSlot.icon}
          </IconButton>
        </Tooltip>
      )}

      {canDownloadPdf && (
        <Tooltip title={t('Download PDF')}>
          <IconButton size="small" onClick={() => onDownloadPdf(invoice)} aria-label={t('Download PDF')}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {isProcessing && (
        <Tooltip title={t('Refresh')}>
          <IconButton size="small" onClick={() => onRefresh(invoice._id)} aria-label={t('Refresh')}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {!isProcessing && (
        <>
          <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label={t('Actions')}>
            <MoreHorizIcon fontSize="small" />
          </IconButton>
          <InvoiceActionMenu
            t={t}
            invoice={invoice}
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={closeMenu}
            onSendEmail={onSendEmail}
            onRemind={onRemind}
            onMarkPaid={onMarkPaid}
            onDownloadPdf={onDownloadPdf}
            onPrintLabel={onPrintLabel}
            onDownloadLabel={onDownloadLabel}
            onEdit={onEdit}
            onVoid={onVoid}
            onDelete={onDelete}
          />
        </>
      )}
    </Box>
  );

  const handleRowKeyDown = (e) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onView(invoice);
    }
  };

  if (isMobile) {
    return (
      <Box
        tabIndex={0}
        onClick={() => onView(invoice)}
        onKeyDown={handleRowKeyDown}
        sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 2, py: 1.5, cursor: 'pointer', ...rowBorderSx }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {identity}
            {created}
          </Box>
          {amount}
        </Box>
        {actions}
      </Box>
    );
  }

  return (
    <Box
      data-tour={tourAnchor ? 'invoices-row' : undefined}
      tabIndex={0}
      onClick={() => onView(invoice)}
      onKeyDown={handleRowKeyDown}
      sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2, cursor: 'pointer', ...rowBorderSx }}
    >
      <Box sx={{ flex: 1.6, minWidth: 0 }}>{identity}</Box>
      {created}
      {amount}
      {actions}
    </Box>
  );
};

export default InvoiceRow;
